import React, { useEffect } from 'react';

import SEO from '../SEO';import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import { CheckCircle, ArrowRight, Building, AlertTriangle, Layers, Shield, HardHat, Ruler } from 'lucide-react';
import Header from '../Header';
import Footer from '../Footer';
import PageHero from '../ui/PageHero';
import { useNavigate } from 'react-router-dom';

const StructuralDamage = () => {
  const navigate = useNavigate();

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  const damageTypes = [
    {
      title: 'Concrete Deterioration',
      description: 'Spalling, cracking, and delamination in concrete structures reducing structural integrity.',
      causes: ['Corrosion of reinforcement', 'Freeze-thaw cycles', 'Chemical attack', 'Overloading'],
      repair: 'Concrete repair mortars and protective coatings'
    },
    {
      title: 'Steel Corrosion',
      description: 'Rust and section loss in structural steel members compromising load capacity.',
      causes: ['Atmospheric exposure', 'Moisture intrusion', 'Chemical environments', 'Coating failure'],
      repair: 'Surface preparation, rebuilding, and protective coatings'
    },
    {
      title: 'Foundation Issues',
      description: 'Cracks, settlement, and water infiltration affecting building stability.',
      causes: ['Soil movement', 'Water damage', 'Poor drainage', 'Tree roots'],
      repair: 'Crack injection, waterproofing, and structural reinforcement'
    },
    {
      title: 'Joint & Connection Failures',
      description: 'Deteriorated expansion joints, anchor bolts, and structural connections.',
      causes: ['Movement and stress', 'Corrosion', 'Age', 'Impact damage'],
      repair: 'Joint sealants, anchor replacement, and composite reinforcement'
    }
  ];

  const warningSignsData = [
    { sign: 'Visible cracks in walls or floors', severity: 'Monitor/Repair' },
    { sign: 'Rust staining on concrete surfaces', severity: 'Inspect Soon' },
    { sign: 'Spalling or flaking concrete', severity: 'Repair Needed' },
    { sign: 'Leaning or bulging walls', severity: 'Urgent' },
    { sign: 'Water infiltration or staining', severity: 'Investigate' },
    { sign: 'Exposed reinforcement steel', severity: 'Immediate Repair' }
  ];

  const repairCapabilities = [
    'Concrete crack injection and sealing',
    'Structural steel rebuilding',
    'Foundation waterproofing',
    'Expansion joint replacement',
    'Anchor bolt repair and replacement',
    'Protective coating application',
    'Carbon fiber reinforcement',
    'Secondary containment lining'
  ];

  const structureTypes = [
    { icon: Building, name: 'Buildings', description: 'Walls, floors, roofs, foundations' },
    { icon: Layers, name: 'Tanks & Vessels', description: 'Storage tanks, containment areas' },
    { icon: Ruler, name: 'Infrastructure', description: 'Bridges, piers, retaining walls' },
    { icon: HardHat, name: 'Industrial', description: 'Equipment pads, trenches, pits' }
  ];

  return (
    <div className="min-h-screen bg-white">
      <Header />
      <SEO title="Structural Damage Solutions" description="Structural damage repair solutions for industrial facilities. Concrete restoration and metal rebuild." path="/research/structural-damage" />
      
      <PageHero
        badge="Research & Learn"
        title="Structural Damage"
        description="Structural damage threatens safety, operations, and asset value. Understanding the causes and early warning signs enables timely intervention before problems escalate."
        backgroundKey="structural-damage"
        buttonText="Get Structural Assessment"
      />

      {/* Damage Types */}
      {/* Damage Types */}
      <section className="py-16 px-4 sm:px-6 lg:px-8">
        <div className="max-w-7xl mx-auto">
          <h2 className="text-3xl font-bold text-slate-900 mb-8 text-center">Types of Structural Damage</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {damageTypes.map((type, index) => (
              <Card key={index} className="border-l-4 border-l-slate-600">
                <CardHeader>
                  <CardTitle className="text-xl">{type.title}</CardTitle>
                </CardHeader>
                <CardContent>
                  <p className="text-gray-600 mb-4">{type.description}</p>
                  <div className="mb-4">
                    <p className="text-sm font-semibold text-slate-700 mb-2">Common Causes:</p>
                    <div className="flex flex-wrap gap-2">
                      {type.causes.map((cause, idx) => (
                        <Badge key={idx} variant="outline" className="text-gray-600">{cause}</Badge>
                      ))}
                    </div>
                  </div>
                  <div className="bg-green-50 p-3 rounded">
                    <p className="text-sm"><span className="font-semibold text-green-700">Our Repair:</span> {type.repair}</p>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* Warning Signs */}
      <section className="py-16 px-4 sm:px-6 lg:px-8 bg-slate-50">
        <div className="max-w-7xl mx-auto">
          <h2 className="text-3xl font-bold text-slate-900 mb-8 text-center">Warning Signs to Watch For</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {warningSignsData.map((item, index) => (
              <div key={index} className="flex items-center justify-between bg-white p-4 rounded-lg shadow-sm">
                <div className="flex items-center">
                  <AlertTriangle className="text-orange-500 mr-3 flex-shrink-0" size={20} />
                  <span className="text-gray-700">{item.sign}</span>
                </div>
                <Badge 
                  className={
                    item.severity === 'Urgent' || item.severity === 'Immediate Repair' 
                      ? 'bg-red-100 text-red-800' 
                      : item.severity === 'Repair Needed' 
                        ? 'bg-orange-100 text-orange-800'
                        : 'bg-yellow-100 text-yellow-800'
                  }
                >
                  {item.severity}
                </Badge>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Structure Types */}
      <section className="py-16 px-4 sm:px-6 lg:px-8">
        <div className="max-w-7xl mx-auto">
          <h2 className="text-3xl font-bold text-slate-900 mb-8 text-center">Structures We Repair</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            {structureTypes.map((structure, index) => {
              const IconComponent = structure.icon;
              return (
                <div key={index} className="text-center p-6 bg-slate-50 rounded-lg">
                  <IconComponent className="mx-auto text-blue-900 mb-3" size={40} />
                  <h3 className="font-bold text-slate-900 mb-2">{structure.name}</h3>
                  <p className="text-sm text-gray-600">{structure.description}</p>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Repair Capabilities */}
      <section className="py-16 px-4 sm:px-6 lg:px-8 bg-blue-900 text-white">
        <div className="max-w-7xl mx-auto">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <div>
              <Shield className="mb-4" size={48} />
              <h2 className="text-3xl font-bold mb-6">Our Structural Repair Capabilities</h2>
              <p className="text-blue-100 mb-8">
                We provide comprehensive structural repair solutions using advanced materials that restore 
                integrity and provide long-term protection against future damage.
              </p>
              <Button className="bg-white text-blue-900 hover:bg-gray-100" onClick={() => navigate('/services/structural-repair')}>
                View Structural Repair Services <ArrowRight className="ml-2" size={16} />
              </Button>
            </div>
            <div className="grid grid-cols-2 gap-3">
              {repairCapabilities.map((capability, index) => (
                <div key={index} className="flex items-center">
                  <CheckCircle className="text-green-400 mr-2 flex-shrink-0" size={18} />
                  <span className="text-blue-100 text-sm">{capability}</span>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-16 px-4 sm:px-6 lg:px-8 bg-orange-600 text-white">
        <div className="max-w-4xl mx-auto text-center">
          <AlertTriangle className="mx-auto mb-4" size={48} />
          <h2 className="text-3xl font-bold mb-4">Don't Ignore Structural Issues</h2>
          <p className="text-orange-100 mb-8">
            Small cracks today can become major failures tomorrow. Let our structural specialists assess your facility.
          </p>
          <Button size="lg" className="bg-white text-orange-600 hover:bg-gray-100" onClick={() => navigate('/#contact')}>
            Schedule Structural Assessment <ArrowRight className="ml-2" size={20} />
          </Button>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default StructuralDamage;
